"use client";

import {
  ChangeEventHandler,
  FocusEventHandler,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { InputRef } from "rc-input";
import { TextAreaRef } from "rc-textarea";
import { useMemoizedFn } from "ahooks";
import {
  InputFieldInstance,
  InputFieldProps,
  OInputSizeType,
  TextAreaFieldInstance,
  TextAreaFieldProps,
} from "./types";
import { useStyles } from "./styles";
import { ospThemes } from "../ospThemes";

/** 与 themes.json / Uno `body_*` 规则对齐 */
const SIZE_TEXT_CLASS: Record<OInputSizeType, string> = {
  small: "body_m",
  large: "body_l",
  huge: "body_l",
};

export const useOspInputField = <T extends InputRef | TextAreaRef>(
  props: InputFieldProps | TextAreaFieldProps,
  ref: React.Ref<InputFieldInstance | TextAreaFieldInstance>,
) => {
  const { size = "small", disabled = false, borderVisible = true, placeholderColor, focusBorderColor, autoFocus = false } = props;
  const [focusState, setFocusState] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const inputRef = useRef<T>(null);

  // 处理自动聚焦
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const styleResult = useStyles({
    focusState,
    errorState,
    disable: disabled,
    size: size,
    borderVisible,
    placeholderColor,
    focusBorderColor,
  });
  // @ts-expect-error - 类型兼容性问题
  useImperativeHandle(ref, () => {
    // 确保返回的对象类型符合 InputFieldInstance 或 TextAreaFieldInstance 接口
    return {
      setError: setErrorState,
      ...inputRef.current!,
    };
  });

  const onFocus: FocusEventHandler<any> = useMemoizedFn((event) => {
    props.onFocus?.(event);
    setFocusState(true);
  });

  const onBlur: FocusEventHandler<any> = useMemoizedFn((event) => {
    props.onBlur?.(event);
    setFocusState(false);
  });

  const onChangeText: ChangeEventHandler<any> = useMemoizedFn((event) => {
    let valueStr: string = event.target.value;
    if (props.formatter) {
      valueStr = props.formatter?.(valueStr);
    }
    if (props.onChange) {
      props.onChange(
        Object.assign({}, event, {
          target: { ...event.target, value: valueStr },
        }),
      );
    }
  });

  // 创建内联样式对象以确保 textarea 在 CSS-in-JS 加载前就有正确的样式
  const inlineTextareaStyles: React.CSSProperties = {
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
    width: "100%",
    minHeight: "18px",
    caretColor: ospThemes.colors.content_brand_primary,
    resize: "none" as const,
  };

  return {
    ...styleResult,
    sizeTextClassName: SIZE_TEXT_CLASS[size],
    inputRef,
    onFocus,
    onBlur,
    onChangeText,
    errorState,
    inlineTextareaStyles,
  };
};
